/*
 * Copyright (c) 2026. Bluemate B.V.
 */
import './style/mobileApp.scss';

import React, {useEffect, useRef} from 'react';
import {useTranslation} from 'react-i18next';
import {Icon} from '@iconify/react';
import Image from '../misc/image';
import {BluemateTabs} from '../controls';
import AndroidInstallBanner from '../pwa/androidInstallBanner';
import InfoBanner from '../misc/infoBanner';

export type MobilePlatform = 'apple' | 'android';

interface Props {
    platform: MobilePlatform;
    onSelectPlatform: (platform: MobilePlatform) => void;
    // True only on a real Android device that has NOT installed the app yet —
    // gates the live-install-banner step (never shown on desktop; there the
    // Android tab keeps just two steps).
    showAndroidBanner: boolean;
    // The page is being viewed from inside the installed app. Only then is the
    // "you already have it" note shown: a browser tab cannot tell an installed
    // app from an uninstalled one on iOS (separate storage, no uninstall event),
    // and a wrong claim there outlives the app for as long as it is cached.
    runningAsApp: boolean;
    // Arrived from the banner shown when the browser withholds its one-tap
    // install: the menu route is the only one that works, so bring it into view
    // instead of leaving the reader to scroll past an option they cannot use.
    focusMenuInstall?: boolean;
}

interface Step {
    id: string;
    text: string;
    // Optional screenshot (flat in res/img — the res/img glob is non-recursive).
    img?: string;
    // Optional live node rendered under the step text (e.g. the install banner).
    node?: React.ReactNode;
}

export default function MobileApp(props: Props) {
    const {t} = useTranslation();
    const {platform, onSelectPlatform, showAndroidBanner, runningAsApp, focusMenuInstall} = props;
    const menuOptionRef = useRef<HTMLElement | null>(null);

    useEffect(() => {
        if (!focusMenuInstall || platform !== 'android') return;
        menuOptionRef.current?.scrollIntoView({behavior: 'smooth', block: 'start'});
    }, [focusMenuInstall, platform]);

    const iosSteps: Step[] = [
        {
            id: 'ios-1',
            text: t(
                'mobileApp.ios.step1',
                'Tik in Safari onderaan het scherm op het deel-icoon (een vierkantje met een pijl omhoog).'
            ),
            img: 'pwa-ios-step-1'
        },
        {
            id: 'ios-2',
            text: t('mobileApp.ios.step2', "Scroll omlaag en kies 'Zet op beginscherm' (Add to Home Screen)."),
            img: 'pwa-ios-step-2'
        },
        {
            id: 'ios-3',
            text: t(
                'mobileApp.ios.step3',
                "Bevestig met 'Voeg toe' (Add). Bluemate verschijnt daarna als app-icoon op je beginscherm."
            ),
            img: 'pwa-ios-step-3'
        }
    ];

    const androidBannerSteps: Step[] = [
        {
            id: 'android-1',
            text: t(
                'mobileApp.android.step1',
                'Open Bluemate in een browser op basis van Chromium, zoals Chrome, Brave of Edge.'
            )
        },
        {
            id: 'android-2',
            text: t(
                'mobileApp.android.step2',
                "Tik in de banner 'Bluemate als app installeren' bovenaan op 'Installeren'. Bluemate verschijnt daarna als app op je startscherm."
            ),
            img: 'pwa-android-banner'
        }
    ];
    // The live install banner (adequate to the browser) is only meaningful on an
    // actual Android device — never on desktop, where the tab keeps two steps.
    if (showAndroidBanner)
        androidBannerSteps.push({
            id: 'android-3',
            text: t('mobileApp.android.step3', 'Volg de banner hieronder om Bluemate te installeren:'),
            node: <AndroidInstallBanner />
        });

    // Route two: Chrome's own menu. Always available — the browser offers the
    // menu entry even in the states where it withholds the automatic prompt, so
    // this is the option that never leaves the user stuck.
    const androidMenuSteps: Step[] = [
        {
            id: 'android-menu-1',
            text: t('mobileApp.android.menuStep1', 'Tik rechtsboven in Chrome op het menu met de drie puntjes (⋮).'),
            img: 'pwa-android-menu-1'
        },
        {
            id: 'android-menu-2',
            text: t(
                'mobileApp.android.menuStep2',
                "Kies 'App installeren'. Heet het bij jou 'Installeren en snelkoppeling maken' of 'Toevoegen aan startscherm'? Dat is dezelfde ingang."
            ),
            img: 'pwa-android-menu-2'
        },
        {
            id: 'android-menu-3',
            text: t(
                'mobileApp.android.menuStep3',
                "Krijg je de keuze, kies dan 'Installeren' en bevestig — niet 'Snelkoppeling maken'. Bluemate verschijnt met het eigen icoon op je startscherm."
            ),
            img: 'pwa-android-menu-3'
        }
    ];

    /** Shown instead of "follow the steps" once the app is installed here —
     *  the Android step 3 rendered the live install banner, which hides itself
     *  after installing and left the step pointing at nothing. Not dismissable
     *  (no `onClose`, `persistDismiss={false}`): it states the current state of
     *  this device, so hiding it would leave the steps looking mandatory. */
    function renderInstalledNote() {
        if (!runningAsApp) return null;
        return (
            <InfoBanner className='install-done-banner' variant='success' persistDismiss={false}>
                {t(
                    'mobileApp.alreadyInstalled',
                    'Je gebruikt Bluemate nu als geïnstalleerde app. De stappen hieronder zijn alleen nog ter naslag — bijvoorbeeld om Bluemate ook op een ander toestel te installeren.'
                )}
            </InfoBanner>
        );
    }

    function renderTimeline(steps: Step[], shotAlt: string) {
        return (
            <ol className='install-timeline'>
                {steps.map((step, index) => (
                    <li key={step.id} className='install-step'>
                        <div className='step-rail'>
                            <span className='step-number'>{index + 1}</span>
                        </div>
                        <div className='step-content'>
                            <span className='step-label'>
                                {t('mobileApp.stepWord', 'Stap')} {index + 1}
                            </span>
                            <p className='step-text'>{step.text}</p>
                            {step.img && (
                                <div className='step-shot'>
                                    <Image name={step.img} alt={shotAlt} />
                                </div>
                            )}
                            {step.node && <div className='step-node'>{step.node}</div>}
                        </div>
                    </li>
                ))}
            </ol>
        );
    }

    /** Chrome's own menu can end in a plain bookmark ("create shortcut") instead
     *  of a WebAPK install — same gesture, completely different result: browser
     *  icon, opens in a tab, no push. Users cannot tell the two apart from the
     *  menu wording, so spell it out next to the steps. */
    function renderShortcutWarning() {
        return (
            <InfoBanner
                className='install-shortcut-warning'
                variant='warning'
                persistDismiss={false}
                title={t('mobileApp.android.shortcutWarningTitle', 'Kies "Installeren", niet "Snelkoppeling maken"')}
            >
                <span className='shortcut-warning-line'>
                    {t(
                        'mobileApp.android.shortcutWarning',
                        "Biedt je browser de keuze tussen installeren en een snelkoppeling maken? Kies dan altijd 'Installeren' (of 'App installeren'). Een snelkoppeling is alleen een bladwijzer naar de website: die krijgt het browser-icoon in plaats van het Bluemate-icoon, opent in een browsertab en kan géén push-meldingen ontvangen. Het zekerst is de knop 'Installeer' in de banner hierboven — die installeert altijd de echte app."
                    )}
                </span>
                <span className='shortcut-warning-line'>
                    {t(
                        'mobileApp.android.shortcutFix',
                        'Al per ongeluk een snelkoppeling gemaakt? Verwijder die van je startscherm en installeer opnieuw via de knop hierboven.'
                    )}
                </span>
            </InfoBanner>
        );
    }

    function renderApple() {
        return (
            <>
                <h2 className='install-heading'>{t('mobileApp.ios.title', 'Installeren op iPhone of iPad')}</h2>
                <p className='install-note'>
                    <Icon icon='mdi:apple' className='install-note-icon' />
                    {t('mobileApp.safariChip', 'Alleen via Safari op iPhone of iPad')}
                </p>
                {renderTimeline(iosSteps, t('mobileApp.ios.shotAlt', 'Bluemate installatiestap'))}
            </>
        );
    }

    function renderOption(props: {
        index: number;
        title: string;
        intro: string;
        children: React.ReactNode;
        sectionRef?: React.Ref<HTMLElement>;
    }) {
        return (
            <section ref={props.sectionRef} className='install-option'>
                <div className='option-header'>
                    <span className='option-badge'>
                        {t('mobileApp.android.optionWord', 'Optie')} {props.index}
                    </span>
                    <h3 className='option-title'>{props.title}</h3>
                </div>
                <p className='option-intro'>{props.intro}</p>
                {props.children}
            </section>
        );
    }

    /** Two routes, both shown at once. The browser decides which one is open to a
     *  given user — Chrome withholds the automatic prompt in more situations than
     *  it admits to — and a user staring at a missing banner has no way of
     *  knowing the menu route exists unless it is on screen next to it. */
    function renderAndroid() {
        const shotAlt = t('mobileApp.android.shotAlt', 'Bluemate installatiestap op Android');
        return (
            <>
                <h2 className='install-heading'>{t('mobileApp.android.title', 'Installeren op Android')}</h2>
                <p className='install-note'>
                    <Icon icon='mdi:google-chrome' className='install-note-icon' />
                    {t('mobileApp.android.browsersNote', 'Chrome, Brave, Edge en andere Chromium-browsers')}
                </p>

                {renderOption({
                    index: 1,
                    title: t('mobileApp.android.bannerOptionTitle', 'Met één tik, via de banner'),
                    intro: t(
                        'mobileApp.android.bannerOptionIntro',
                        'De snelste weg: je browser opent de installatiedialoog meteen.'
                    ),
                    children: (
                        <>
                            {renderTimeline(androidBannerSteps, shotAlt)}
                            <p className='option-fallback'>
                                <Icon icon='mdi:information-outline' className='option-fallback-icon' />
                                {t(
                                    'mobileApp.android.bannerOptionFallback',
                                    'Zie je de banner niet? Je browser biedt de knop niet altijd aan. Gebruik dan optie 2 hieronder — die werkt altijd.'
                                )}
                            </p>
                        </>
                    )
                })}

                {renderOption({
                    index: 2,
                    sectionRef: menuOptionRef,
                    title: t('mobileApp.android.menuOptionTitle', 'Via het menu van je browser'),
                    intro: t(
                        'mobileApp.android.menuOptionIntro',
                        'Werkt altijd, ook als de banner er niet staat. Let wel goed op de laatste stap.'
                    ),
                    children: (
                        <>
                            {renderTimeline(androidMenuSteps, shotAlt)}
                            {renderShortcutWarning()}
                        </>
                    )
                })}
            </>
        );
    }

    return (
        <div className='mobile-app'>
            <header className='mobile-app-hero'>
                <div className='hero-text'>
                    <span className='hero-eyebrow'>{t('mobileApp.eyebrow', 'Mobiele app')}</span>
                    <h1 className='hero-title'>{t('mobileApp.title', 'Bluemate als app op je telefoon')}</h1>
                    <p className='hero-intro'>
                        {t(
                            'mobileApp.intro',
                            'Installeer Bluemate als app op je telefoon. Als geïnstalleerde app start Bluemate sneller en ontvang je push-meldingen over koopblok-wissels, lage voorraad en nieuwe reviews. Kies hieronder je toestel voor de stappen.'
                        )}
                    </p>
                </div>
                <div className='hero-visual'>
                    <Image name='logo-no-text-big' alt='' className='hero-logo' />
                </div>
            </header>

            <section className='install-section'>
                <BluemateTabs<MobilePlatform>
                    className='platform-tabs'
                    activeTab={platform}
                    onChangeTab={tab => onSelectPlatform(tab)}
                    tabs={[
                        {
                            key: 'apple',
                            label: (
                                <>
                                    <Icon icon='mdi:apple' />
                                    {t('mobileApp.platformApple', 'iPhone / iPad')}
                                </>
                            )
                        },
                        {
                            key: 'android',
                            label: (
                                <>
                                    <Icon icon='mdi:android' />
                                    {t('mobileApp.platformAndroid', 'Android')}
                                </>
                            )
                        }
                    ]}
                />
                {renderInstalledNote()}
                {platform === 'apple' ? renderApple() : renderAndroid()}
            </section>
        </div>
    );
}
